import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Switch, Route } from 'react-router-dom';
import HomePage from './HomePage';
import LoginPage from './Login/LoginPage';
import LogoutPage from './Logout/LogoutPage';
import CorporateDashboard from './CorporateDashboard/CorporateDashboardPage';
import CookiePolicyNotification from './CookiePolicyNotification/CookiePolicyNotification';
import LocationDashboard from './LocationDashboard/LocationDashboardPage';
import LocationFinancialsSpreadsheet from './LocationDashboard/LocationFinancialsSpreadsheet';
import AdminDashboard from './AdminDashboard/AdminDashboardPage';
import SubscriptionPage from './Subscription/SubscriptionPage';
import StyleGuide from './StyleGuide/StyleGuidePage';
import TaskListPage from './TaskList/TaskListPage/TaskListPage';
import ReportViewPage from './Reports/ReportViewPage';
import ScheduleReportPage from './Reports/ScheduleReportPage';
import NotFoundPage from './404/NotFoundPage';
import Forbidden from './Forbidden';
import PrivateRoute from './PrivateRoute';
import IdleTimer from './IdleTimer/IdleTimer';
import 'bootstrap';
import { EH_ADMIN_ROLES, CUSTOMER_ROLES } from '../constants/roles';
import Routes from '../constants/routes';
import FreeTrialSignUp from './Login/FreeTrialSignUp';
import ForgotUsernamePage from './ForgotUsername/ForgotUsernamePage';
import ForgotPasswordPage from './ForgotPassword/ForgotPasswordPage';
import ResetPasswordPage from './ResetPassword/ResetPasswordPage';
import SubscriptionConfigPage from './Subscription/SubscriptionConfigPage';
import SubscriptionUsersPage from './Subscription/SubscriptionUsersPage';
import GlobalUsersPage from './Subscription/GlobalUsersPage';
import LocationsListPage from './Locations/LocationsListPage';
import SubscriptionAdminPage from './Subscription/SubscriptionAdminPage';
import BaselineTaskListController from './BaselineTaskList/BaselineTaskListController';
import WindowResizeListener from './WindowResizeListener/WindowResizeListener';
import MyProfile from './MyProfile/MyProfile';
import NotificationSettings from './NotificationSettings/NotificationSettingsPage';
import ProjectLocationsPage from './Subscription/Admin/ProjectLocations/ProjectLocationsPage';
import CrossBaselineSearchPage from './Subscription/Admin/CrossBaselineSearchPage';
import CrossBaselineTaskLinkSearch from './Subscription/Admin/CrossBaselineTaskLink/CrossBaselineTaskLinkSearch';
import ProjectMgrDashboardPage from './ProjectMgrDashboard/ProjectMgrDashboardPage';
import SsoErrorPage from './Login/SsoErrorPage';
import SignUpPage from './Login/UserAccessRequest/UserAccessRequestWizard';
import ExternalTaskCompletion from './External/TaskCompletion';
import ToolTips from './AdminDashboard/ToolTips';
import WebAssets from './AdminDashboard/WebAssets';
import DocumentTitle from 'react-document-title';
import 'url-search-params-polyfill';
import MobileAppPopup from './MobileAppPopup/MobileAppPopup';
import GA4React from 'ga-4-react';
import NewProjectWizardPage from './NewProjectWizard/NewProjectWizardPage';
import MyWorkspace from './MyWorkspace/MyWorkspace';
import ReportsPage from './ReportsPage/ReportsPage';
import PMDashboard from './PMDashboard/PMDashboard';
import ManageTaskViews from './TaskList/ManageTaskViews';
import ManageCorporateViews from './CorporateDashboard/ManageCorporateViews';

// This is a class-based component because the current
// version of hot reloading won't hot reload a stateless
// component at the top-level.
class App extends React.Component {
	componentDidMount = async () => {
		// eslint-disable-next-line no-undef
		const google4TrackingId = __GOOGLE_ANALYTICS_4_KEY__ ? __GOOGLE_ANALYTICS_4_KEY__ : '1234567890'; //this is a dummy key to prevent errors on the console
		const ga4react = new GA4React(
			google4TrackingId,
			{
				/* ga custom config, optional */
			},
			[
				/* additional code, optional */
			],
			5000 /* timeout, optional, defaults is 5000 */
			//options /* { nonce: ['first-script-is-async','second-script'] } */
		);

		ga4react
			.initialize()
			.then((ga4) => {
				ga4.pageview('path');
				//ga4.gtag('event','pageview','path') // or your custom gtag event
			})
			.catch((err) => {
				console.error('GA4 initialization failed', err);
			});
	};

	render() {
		// eslint-disable-next-line no-undef
		const ehAdminRoles = [EH_ADMIN_ROLES.SuperAdministrator.name, EH_ADMIN_ROLES.SystemAdministrator.name];
		const customerAdminRoles = [CUSTOMER_ROLES.Admin.name, CUSTOMER_ROLES.Moderator.name, CUSTOMER_ROLES.Owner.name];

		return (
			<div>
				<MobileAppPopup />
				<Switch>
					<Route path={Routes.LoginPage} component={LoginPage} />
					<Route path={Routes.LogoutPage} component={LogoutPage} />
					<Route path={Routes.ForgotUserName} component={ForgotUsernamePage} />
					<Route path={Routes.ForgotPassword} component={ForgotPasswordPage} />
					<Route path={Routes.ResetPassword} component={ResetPasswordPage} />
					<Route path={Routes.SignUp} component={SignUpPage} />
					<Route path={Routes.StyleGuide} component={StyleGuide} />
					<Route path={Routes.SsoErrorPage} component={SsoErrorPage} />
					<Route path={Routes.External.TaskCompletion} component={ExternalTaskCompletion} />
					<Route path={Routes.FreeTrialSignUp + ':template?'} component={FreeTrialSignUp} />

					<PrivateRoute exact path={Routes.Root} component={HomePage} />
					<PrivateRoute path={Routes.ProfileNotifications} component={NotificationSettings} />
					<PrivateRoute path={Routes.ProfileUser} component={MyProfile} />
					<PrivateRoute path={Routes.DashboardCorporate} component={CorporateDashboard} />
					<PrivateRoute
						roles={customerAdminRoles}
						exact
						path={Routes.Admin + '/project/:projectKey/project-locations'}
						component={ProjectLocationsPage}
					/>
					<PrivateRoute path={Routes.DashboardLocation} exact component={LocationDashboard} />
					<PrivateRoute path={Routes.ManageCorporateViews} component={ManageCorporateViews} />
					<PrivateRoute path={Routes.DashboardLocationFinancials} exact component={LocationFinancialsSpreadsheet} />
					<PrivateRoute path={Routes.ManageTaskViews} component={ManageTaskViews} />
					<PrivateRoute path={Routes.Tasks} component={TaskListPage} />

					<PrivateRoute path={Routes.AdminHome} exact roles={customerAdminRoles} component={SubscriptionAdminPage} />
					<PrivateRoute path={Routes.AdminHome + '/:section'} roles={customerAdminRoles} component={SubscriptionAdminPage} />

					<PrivateRoute path={Routes.Reports} exact component={ReportsPage} />
					<PrivateRoute path={Routes.Reports + '/:reportId'} exact component={ReportViewPage} />
					<PrivateRoute path={Routes.ScheduleReport + '/:reportId'} exact component={ScheduleReportPage} />
					<PrivateRoute path={Routes.ScheduleReport + '/edit/:scheduledReportId'} exact component={ScheduleReportPage} />

					<PrivateRoute path={Routes.BaselineTaskList} component={BaselineTaskListController} />
					<PrivateRoute roles={customerAdminRoles} path={Routes.Admin + '/pm-dashboard/:projectKey?'} component={ProjectMgrDashboardPage} />
					<PrivateRoute roles={customerAdminRoles} path={Routes.Admin + '/project-dashboard/:projectKey?'} component={PMDashboard} />

					<PrivateRoute roles={ehAdminRoles} path={Routes.DashboardEhAdmin} component={AdminDashboard} />
					<PrivateRoute roles={ehAdminRoles} exact path='/subscription/:subscriptionId/locations' component={LocationsListPage} />
					<PrivateRoute roles={ehAdminRoles} path='/subscription/config/:subscriptionId?' component={SubscriptionConfigPage} />
					<PrivateRoute roles={ehAdminRoles} path={Routes.CrossSubscriptionSearch} component={GlobalUsersPage} />
					<PrivateRoute roles={ehAdminRoles} path='/subscription/users/:subscriptionId?' component={SubscriptionUsersPage} />
					<PrivateRoute roles={ehAdminRoles} path={Routes.ToolTips} component={ToolTips} />
					<PrivateRoute roles={ehAdminRoles} path={Routes.WebAssets} component={WebAssets} />
					<PrivateRoute roles={ehAdminRoles} path='/subscription/:subscriptionId?' component={SubscriptionPage} />

					<PrivateRoute path={Routes.CrossBaselineSearch} component={CrossBaselineSearchPage} />

					<PrivateRoute path={Routes.CrossBaselineTaskLinkSearch} component={CrossBaselineTaskLinkSearch} />
					<PrivateRoute path={Routes.MyWorkspace} component={MyWorkspace} />
					<PrivateRoute path={Routes.NewProjectWizard} roles={customerAdminRoles} component={NewProjectWizardPage} />

					<PrivateRoute component={NotFoundPage} />
					<PrivateRoute exact path={Routes.Forbidden} component={Forbidden} />
				</Switch>
				<CookiePolicyNotification />
				<IdleTimer />
				<WindowResizeListener />
				<DocumentTitle title='Pacer' />
			</div>
		);
	}
}

App.propTypes = {
	user: PropTypes.object, // Ensure subscriptionId is available as a prop
	children: PropTypes.element,
};

const mapStateToProps = (state) => ({
	user: state.user, // Adjust this to match your state structure
});

export default connect(mapStateToProps)(App);
